import { useNavigate } from '@tanstack/react-router';
import { useCallback, useMemo, useState } from 'react';

import {
  useDeleteProfileList,
  useExcludeList,
  useSavedListLists,
  useUnExcludeCreator,
  type CreatorsInListDTO,
} from '@/entities/saved-creators-list';

import { getPlatformName } from '@/entities/public-filter';
import { AnimatedSuspense } from '@/shared/components/animated-suspense';
import { BaseTable, type TableColumnT } from '@/shared/components/base-table';
import { PageLoader } from '@/shared/components/loader/page-loader';
import { ProfileImage } from '@/shared/components/profile-image';
import { Badge } from '@/shared/components/shadcn/badge';
import { Button } from '@/shared/components/shadcn/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/shared/components/shadcn/dropdown-menu';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/shared/components/shadcn/tabs';
import { useWorkspaceId } from '@/shared/hooks/use-workspace-id';
import { DATE_FORMATS } from '@/shared/utils/date/date-formats';
import { formatNumber } from '@/shared/utils/format-number';
import { showErrorDialog } from '@/shared/utils/show-error-dialog';
import dayjs from 'dayjs';
import { Download, Edit, Ellipsis, ExternalLink, Trash2 } from 'lucide-react';
import { overlay } from 'overlay-kit';
import { toast } from 'sonner';
import { EditListNameDialog } from './overlays/edit-list-name-dialog';
import { SavedProfileListViewHeader } from './saved-profiles.header';
import { useExportDiscoveryCreatorListWithFeedback } from '@/entities/discovery';
import { ExportExcludedCreatorList } from '@/entities/discovery/api/export-excluded-creator-list';

export function SavedProfilesPage() {
  const [activeTab, setActiveTab] = useState('saved');

  return (
    <div className="flex h-screen flex-col">
      <SavedProfileListViewHeader />
      <div className="flex flex-1 flex-col overflow-auto">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="flex flex-1 flex-col p-4">
          <TabsList className="grid grid-cols-2">
            <TabsTrigger value="saved">Saved</TabsTrigger>
            <TabsTrigger value="excluded">Excluded</TabsTrigger>
          </TabsList>
          <TabsContent value="saved" className="relative flex-1">
            <AnimatedSuspense
              className="flex h-full flex-1 flex-col"
              fallback={() => <div className="flex-center h-full w-full">Failed to fetch data</div>}
              loader={<PageLoader />}
            >
              <ProfileListTable />
            </AnimatedSuspense>
          </TabsContent>
          <TabsContent value="excluded" className="relative flex-1">
            <AnimatedSuspense
              className="flex h-full flex-1 flex-col"
              fallback={() => <div className="flex-center h-full w-full">Failed to fetch data</div>}
              loader={<PageLoader />}
            >
              <ExcludeListTable />
            </AnimatedSuspense>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}

const ProfileListTable = () => {
  const navigate = useNavigate();
  const { workspaceId } = useWorkspaceId();
  const { data: lists } = useSavedListLists();
  const deleteListMutation = useDeleteProfileList();
  const exportCreatorListWithFeedback = useExportDiscoveryCreatorListWithFeedback();

  const handleEditList = useCallback(async (listId: string, currentTitle: string) => {
    overlay.open(({ isOpen, close }) => {
      if (!isOpen) return null;
      return <EditListNameDialog close={close} currentTitle={currentTitle} listId={listId} />;
    });
  }, []);

  const handleDeleteList = useCallback(
    async (listId: string) => {
      if (confirm('Are you sure you want to delete this list?')) {
        await deleteListMutation.mutateAsync(listId);
      }
    },
    [deleteListMutation],
  );

  const handleListClick = useCallback(
    (listId: string) => {
      navigate({
        to: '/workspace/$workspaceId/saved-profiles/$listId',
        params: { listId, workspaceId },
      });
    },
    [navigate, workspaceId],
  );

  const handleExportList = useCallback(
    async (listId: string, title: string) => {
      await exportCreatorListWithFeedback(listId, title, {
        selected_all: true,
      });
    },
    [exportCreatorListWithFeedback],
  );

  return (
    <div className="flex flex-1 flex-col gap-4 pt-4">
      <BaseTable
        columns={[
          {
            columnKey: 'list_name',
            Header: <>List name</>,
            Cell(rowData) {
              return rowData.rowData.title;
            },
            className: 'flex-1',
          },
          {
            columnKey: 'creators',
            Header: <>Creators</>,
            Cell(rowData) {
              return formatNumber(Number(rowData.rowData.total_count));
            },
            className: 'w-40',
          },
          {
            columnKey: 'created_at',
            Header: <>Created date</>,
            Cell(rowData) {
              return dayjs(rowData.rowData.created_at).format(DATE_FORMATS.base);
            },
            className: 'w-40',
          },
          {
            columnKey: 'actions',
            Header: <></>,
            Cell(rowData) {
              return (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="sm">
                      <Ellipsis className="size-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-40">
                    <DropdownMenuItem onClick={() => handleListClick(rowData.rowKey)}>
                      <ExternalLink className="size-4" />
                      View
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={() => handleExportList(rowData.rowKey, rowData.rowData.title)}
                    >
                      <Download className="size-4" />
                      Export CSV
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={() => handleEditList(rowData.rowKey, rowData.rowData.title)}
                    >
                      <Edit className="size-4" />
                      Edit
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      variant="destructive"
                      onClick={() => handleDeleteList(rowData.rowKey)}
                    >
                      <Trash2 className="size-4" />
                      Delete
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              );
            },
            className: 'w-20',
          },
        ]}
        data={lists.map((list) => {
          return {
            rowKey: list.id,
            rowData: list,
          };
        })}
        onRowClick={(rowKey) => handleListClick(rowKey)}
        placeholder={{
          node: (
            <div className="flex-center mb-20 flex-1">
              <p className="text-muted-foreground text-sm">No saved list yet</p>
            </div>
          ),
        }}
      />
    </div>
  );
};

const ExcludeListTable = () => {
  const { workspaceId } = useWorkspaceId();
  const { data, isLoading, hasNextPage, fetchNextPage, isFetchingNextPage } = useExcludeList();
  const excludedList = useMemo(() => {
    return data.pages.map((p) => p.creators).flat();
  }, [data.pages]);
  const unExcludeProfileMutaion = useUnExcludeCreator();

  const handleExportCreators = async () => {
    try {
      await ExportExcludedCreatorList(workspaceId);
    } catch (error) {
      await showErrorDialog({
        error,
        title: 'Failed to export creators',
      });
    }
  };

  const excludeColumns: TableColumnT<CreatorsInListDTO>[] = [
    {
      columnKey: 'creator',
      Header: <>Creator</>,
      Cell(rowData) {
        return (
          <div className="flex items-center gap-2">
            <ProfileImage
              src={rowData.rowData.profile_image_url}
              name={rowData.rowData.username}
              className="size-8 rounded-full"
            />
            <span>{rowData.rowData.username}</span>
          </div>
        );
      },
      className: 'flex-1',
    },
    {
      columnKey: 'platform',
      Header: <>Platform</>,
      Cell(rowData) {
        return <Badge variant="secondary">{getPlatformName(rowData.rowData.platform)}</Badge>;
      },
      className: 'w-30',
    },
    {
      columnKey: 'followers',
      Header: <>Followers</>,
      Cell(rowData) {
        return formatNumber(rowData.rowData.followers);
      },
      className: 'w-30',
    },
    {
      columnKey: 'excluded_date',
      Header: <>Excluded date</>,
      Cell(rowData) {
        return dayjs(rowData.rowData.excluded_at).format(DATE_FORMATS.base);
      },
      className: 'w-40',
    },
    {
      columnKey: 'action',
      Header: <></>,
      Cell(rowData) {
        return (
          <Button
            variant="ghost"
            onClick={() => {
              unExcludeProfileMutaion.mutateAsync(rowData.rowKey);
              toast('Profile restored');
            }}
          >
            Restore
          </Button>
        );
      },
      className: 'w-30',
    },
  ];

  return (
    <div className="flex flex-1 flex-col gap-4">
      <Button className="self-end" size="sm" variant="outline" onClick={handleExportCreators}>
        <Download />
        Export all as CSV
      </Button>
      <BaseTable
        columns={excludeColumns}
        isLoading={isLoading}
        hasNextPage={hasNextPage}
        fetchNextPage={fetchNextPage}
        isFetchingNextPage={isFetchingNextPage}
        data={excludedList.map((p) => {
          return {
            rowKey: p.id,
            rowData: p,
          };
        })}
        placeholder={{
          node: (
            <div className="flex-center mb-20 flex-1">
              <p className="text-muted-foreground text-center text-sm leading-6">
                No excluded profiles yet
                <br />
                Excluded creators will appear here
              </p>
            </div>
          ),
        }}
      />
    </div>
  );
};
